/**
 * Pure transforms over a studio-config draft.
 *
 * `edit_studio_config` is FULL-REPLACE: the patch a surface sends BECOMES the
 * stored override row. There is a wipe guard, but it only reaches TOP-LEVEL
 * keys — `findStudioConfigWipedField` skips any key that is present and
 * non-null, so a patch supplying `chat_translator: {…}` while dropping the
 * sub-key `send_quick_ack` sails straight through it. `l2-yaml.ts` says as much
 * about `/config` itself: it "POSTs the whole config object and so round-trips
 * the block by accident".
 *
 * Accident is not a mechanism. These transforms exist so a second page can edit
 * one field without inheriting that risk: each takes the WHOLE config, returns
 * the WHOLE config, and changes exactly one path. Nothing here constructs a
 * partial patch, and nothing here can.
 *
 * Both are lifted verbatim from `StudioConfig.tsx`'s inline handlers so the two
 * pages cannot disagree about what "off" or "the default" writes —
 * `StudioConfig.tsx` calls these too.
 */

import {
  CONNECTOR_DESCRIPTORS,
  connectorDefaultCapability,
  type AiAutonomyMode,
  type ConnectorCapability,
  type ConnectorKey,
} from "@layer/canonical-data/connectors";

/**
 * The four states a conversational connector's row can be in. The first is the
 * connector pause; the other three are {@link AiAutonomyMode} values, so this
 * union is deliberately `"off" | AiAutonomyMode` rather than a fourth
 * vocabulary.
 */
export type ConnectorAiState = "off" | AiAutonomyMode;

interface ConnectorEntry {
  enabled?: boolean;
  autonomy?: AiAutonomyMode;
  capability?: ConnectorCapability;
}

/**
 * Set one connector's four-state ladder on a config draft.
 *
 * Each half DROPS its key at that connector's own default, so a studio that
 * never moves off the default persists nothing and matches a fresh studio
 * exactly. Turning the connector OFF leaves the stored autonomy — and the
 * stored capability — untouched, so switching it back on restores the
 * operator's previous choices rather than silently resetting them.
 */
export function applyConnectorState<T extends Record<string, unknown>>(
  config: T,
  key: ConnectorKey,
  next: ConnectorAiState,
): T {
  const defaultMode =
    CONNECTOR_DESCRIPTORS.find((d) => d.key === key)?.ai?.defaultMode ?? "notify";

  const existing = (config["connectors"] ?? {}) as Partial<
    Record<ConnectorKey, ConnectorEntry>
  >;
  const nextConnectors: Partial<Record<ConnectorKey, ConnectorEntry>> = {
    ...existing,
  };
  const entry: ConnectorEntry = { ...(nextConnectors[key] ?? {}) };

  if (next === "off") {
    entry.enabled = false;
  } else {
    // Enabled is the default — drop the flag rather than persisting `true`.
    delete entry.enabled;
    if (next === defaultMode) {
      delete entry.autonomy;
    } else {
      entry.autonomy = next;
    }
  }

  if (Object.keys(entry).length === 0) {
    delete nextConnectors[key];
  } else {
    nextConnectors[key] = entry;
  }

  // Spread the WHOLE config, then replace one key. Every unrelated top-level
  // block — and every sub-key inside them — survives by construction rather
  // than by the author remembering to carry it.
  const draft: Record<string, unknown> = { ...config };
  if (Object.keys(nextConnectors).length === 0) {
    delete draft["connectors"];
  } else {
    draft["connectors"] = nextConnectors;
  }
  return draft as T;
}

/**
 * Set one connector's capability pair on a config draft.
 *
 * Sibling of {@link applyConnectorState} with the same drop-at-default rule:
 * a connector left at its own `defaultCapability` persists nothing. The two
 * transforms touch DISJOINT sub-keys of the same entry (`enabled`/`autonomy`
 * here, `capability` there), so applying one never disturbs the other — that
 * is what lets the off/on toggle preserve a stored capability choice.
 */
export function applyConnectorCapability<T extends Record<string, unknown>>(
  config: T,
  key: ConnectorKey,
  next: ConnectorCapability,
): T {
  // Via the shared reader, NOT `d.ai?.defaultCapability`: a capability-only
  // chat surface (`webchat`) carries its default on the `chat` block, and a
  // hand-written `ai?.` here would have read `undefined` → fallen back to
  // "converse" → persisted `capability: "transact"` as if it were a
  // non-default choice, storing a value identical to the default on every
  // click. Drop-at-default only works if "the default" has one reader.
  const defaultCapability = connectorDefaultCapability(key) ?? "converse";

  const existing = (config["connectors"] ?? {}) as Partial<
    Record<ConnectorKey, ConnectorEntry>
  >;
  const nextConnectors: Partial<Record<ConnectorKey, ConnectorEntry>> = {
    ...existing,
  };
  const entry: ConnectorEntry = { ...(nextConnectors[key] ?? {}) };

  if (next === defaultCapability) {
    delete entry.capability;
  } else {
    entry.capability = next;
  }

  if (Object.keys(entry).length === 0) {
    delete nextConnectors[key];
  } else {
    nextConnectors[key] = entry;
  }

  const draft: Record<string, unknown> = { ...config };
  if (Object.keys(nextConnectors).length === 0) {
    delete draft["connectors"];
  } else {
    draft["connectors"] = nextConnectors;
  }
  return draft as T;
}

/**
 * Set `studio.webchat_enabled` on a config draft.
 *
 * The `studio` block is where the sub-key blind spot bites hardest: it carries
 * ~20 unrelated fields (ops-alert number, template names, brand voice, opening
 * hours), and the wipe guard sees only that `studio` is present. Spreading the
 * existing block is what keeps them.
 *
 * `false` is written explicitly rather than dropped: absent means OFF at the
 * endpoint, so dropping the key would also turn it off — but it would be
 * indistinguishable from "never configured", and an operator who turns webchat
 * off should be able to see that they did.
 */
export function applyWebchatEnabled<T extends Record<string, unknown>>(
  config: T,
  enabled: boolean,
): T {
  const studio = (config["studio"] ?? {}) as Record<string, unknown>;
  const draft: Record<string, unknown> = { ...config };
  draft["studio"] = { ...studio, webchat_enabled: enabled };
  return draft as T;
}
